// Empire Apps Chassis · connectors/financial/sms-inbox-android
// Android SMS inbox parser — Capacitor bridge (mobile-shell only).
// On web/desktop: graceful stub.
//
// Recognises bank UPI + transaction alerts from common SMS senders:
//   HDFCBK, ICICIB, SBIINB, AXISBK, KOTAKB, SBIUPI, JIOMONEY etc.
// Returns BankTransaction[] derived from SMS body.

import {
  type BankTransaction,
  type Connector,
  type ConnectorContext,
  type ConnectorPayload,
  type ConnectorResult,
  type ConnectorStatus,
  accountId,
  fail,
  isoTs,
  ok,
  paise,
} from './types.ts';
import { logConnectorConsent } from './dpdp-purposes.ts';
import { kvReadJson, kvWriteJson } from './storage-kv.ts';

const STATUS_KEY = 'empire:financial:sms-inbox:status';

interface PersistedStatus {
  readonly connected: boolean;
  readonly lastSync: string | null;
  readonly importedCount: number;
}

function readStatus(): PersistedStatus {
  return kvReadJson<PersistedStatus>(STATUS_KEY, { connected: false, lastSync: null, importedCount: 0 });
}

function writeStatus(s: PersistedStatus): void {
  kvWriteJson(STATUS_KEY, s);
}

interface SmsRow {
  readonly sender: string;
  readonly body: string;
  readonly date: number;
}

export function parseTransactionSms(rows: readonly SmsRow[]): readonly BankTransaction[] {
  const out: BankTransaction[] = [];
  for (const row of rows) {
    const senderUC = row.sender.toUpperCase();
    if (!/HDFC|ICICI|SBI|AXIS|KOTAK|IDFC|YESB|AU|JIO|UPI|PAYT|PHON|GPAY/i.test(senderUC)) continue;
    const amtMatch = row.body.match(/(?:Rs\.?|INR|₹)\s*([\d,]+(?:\.\d{1,2})?)/i);
    if (!amtMatch) continue;
    const amtPaise = Math.round(Number(amtMatch[1]?.replace(/,/g, '') ?? '0') * 100);
    if (!amtPaise) continue;
    const isCredit = /(?:credit|received|cr\b|added)/i.test(row.body);
    const merchantMatch = row.body.match(/(?:to|at|@)\s+([A-Z][A-Za-z0-9 .&'_-]{2,40})/);
    const merchant = merchantMatch?.[1]?.trim() ?? null;
    out.push({
      txId: `sms_${row.date}`,
      accountId: accountId(`acct_sms_${senderUC.slice(0, 4)}`),
      amountPaise: paise(amtPaise),
      direction: isCredit ? 'credit' : 'debit',
      narration: row.body.slice(0, 120),
      merchant,
      category: null,
      bookedAt: new Date(row.date).toISOString() as ReturnType<typeof isoTs>,
      mode: /UPI/i.test(row.body) ? 'upi' : 'unknown',
    });
  }
  return out;
}

interface CapacitorSms {
  readSms?: (opts: { readonly limit: number }) => Promise<{ readonly items: readonly SmsRow[] }>;
}

function getSmsBridge(): CapacitorSms | null {
  const cap = (globalThis as unknown as { Capacitor?: { Plugins?: { Sms?: CapacitorSms } } }).Capacitor;
  return cap?.Plugins?.Sms ?? null;
}

export const smsInboxAndroid: Connector = {
  id: 'sms-inbox-android',
  displayName: 'Android SMS inbox (transaction alerts)',
  category: 'sms-inbox',
  defaultMode: 'mock',

  async status(): Promise<ConnectorStatus> {
    const s = readStatus();
    const bridgeAvailable = getSmsBridge() !== null;
    return {
      id: this.id,
      displayName: this.displayName,
      category: this.category,
      mode: 'mock',
      connected: s.connected,
      lastSync: (s.lastSync as ReturnType<typeof isoTs> | null) ?? null,
      accountCount: s.importedCount,
      disabledReason: bridgeAvailable ? null : 'Android shell required. Web/desktop ships in mock mode.',
    };
  },

  async connect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly connectedAt: ReturnType<typeof isoTs> }>> {
    logConnectorConsent(ctx.dpdpLog, 'connector-sms-inbox-android', 'Android SMS read');
    const connectedAt = isoTs();
    writeStatus({ connected: true, lastSync: connectedAt, importedCount: 0 });
    return ok({ connectedAt });
  },

  async import(): Promise<ConnectorResult<ConnectorPayload>> {
    const s = readStatus();
    if (!s.connected) return fail('Not connected.', true);
    const bridge = getSmsBridge();
    if (bridge?.readSms) {
      try {
        const { items } = await bridge.readSms({ limit: 500 });
        const txs = parseTransactionSms(items);
        writeStatus({ ...s, lastSync: isoTs(), importedCount: txs.length });
        return ok({ transactions: txs });
      } catch (e) {
        return fail(`SMS read failed: ${(e as Error).message}`, true);
      }
    }
    const mockRows: readonly SmsRow[] = [
      { sender: 'HDFCBK', body: 'Rs.499.00 debited via UPI from a/c XX2810 to SWIGGY@hdfcbank on 10-May-26. Bal Rs.84,200.', date: Date.now() - 86_400_000 },
      { sender: 'ICICIB', body: 'INR 85,000.00 credited to your A/c XXX1234 SALARY 11-May-26.', date: Date.now() - 3 * 86_400_000 },
      { sender: 'SBIUPI', body: 'Rs.149.00 paid to BLINKIT@paytm on 12-May-26 via UPI Ref 419283. Bal Rs.41,200.', date: Date.now() - 2 * 86_400_000 },
    ];
    const txs = parseTransactionSms(mockRows);
    writeStatus({ ...s, lastSync: isoTs(), importedCount: txs.length });
    return ok({ transactions: txs });
  },

  async disconnect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly disconnectedAt: ReturnType<typeof isoTs> }>> {
    writeStatus({ connected: false, lastSync: null, importedCount: 0 });
    ctx.dpdpLog('connector-consent-withdrawn', { purpose: 'connector-sms-inbox-android' });
    return ok({ disconnectedAt: isoTs() });
  },

  mockData(): ConnectorPayload {
    return { transactions: [] };
  },
};
